import { WarningCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const TYPO_COLS = new Set([
  "insured_asset", "insured_entity", "type", "category", "type_code",
  "carrier", "broker", "broker_phone", "broker_email",
]);

function levenshtein(a, b) {
  a = a.toLowerCase(); b = b.toLowerCase();
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[b.length];
}

// Returns the existing spelling when `input` is a near-miss (edit distance ≤ 2) of a known value; null for exact or unrelated values.
export function findTypoMatch(input, options) {
  if (!input || !options?.length) return null;
  const norm = String(input).trim().toLowerCase();
  if (!norm || options.some((o) => o.trim().toLowerCase() === norm)) return null;
  const best = options.map((o) => ({ value: o, d: levenshtein(norm, o.trim().toLowerCase()) })).sort((x, y) => x.d - y.d)[0];
  if (!best) return null;
  const len = Math.max(norm.length, best.value.length);
  return best.d <= 2 && best.d / Math.max(1, len) < 0.35 ? best.value : null;
}

export function TypoWarningDialog({ prompts, onCancel }) {
  return (
    <Dialog open={!!prompts} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="insurance-typo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <WarningCircle size={18} weight="fill" /> Possible typo detected
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-700 space-y-2">
          <p>We found one or more field values that look like a slight typo of an existing entry. Using the existing spelling keeps filters and reports accurate.</p>
          <div className="mt-2 space-y-2">
            {prompts?.entries?.map((e) => (
              <div key={e.col} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px]" data-testid={`insurance-typo-entry-${e.col}`}>
                <div className="uppercase tracking-widest font-semibold text-amber-800 text-[10px]">{e.label}</div>
                <div className="mt-0.5">You typed <span className="font-semibold">"{e.input}"</span> — did you mean <span className="font-bold text-emerald-700">"{e.suggestion}"</span>?</div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onCancel} data-testid="insurance-typo-cancel">Cancel</Button>
          <Button variant="outline" onClick={() => prompts?.onProceed?.()} data-testid="insurance-typo-keep">Keep my spelling</Button>
          <Button onClick={() => prompts?.onAcceptAll?.()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="insurance-typo-accept">
            Use suggested{prompts?.entries?.length > 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
